import React, { useState } from 'react';
import './DepositModal.css';
import { useUser } from '../../context/UserContext';

const DepositModal = ({ isOpen, onClose, currency = 'BTC' }) => {
  const { addDeposit } = useUser();
  const [amount, setAmount] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [depositSubmitted, setDepositSubmitted] = useState(false);
  const [depositId, setDepositId] = useState('');

  // Mock BTC address for demonstration
  const btcAddress = 'bc1qxy2kgdygjrsqtzq2n0yrf2493p83kkfjhx0wlh';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) return;

    setIsSubmitting(true);

    try {
      // Create deposit request via API
      const response = await addDeposit({
        amount: parseFloat(amount),
        paymentMethod: currency.toLowerCase(),
        cryptoAmount: currency === 'BTC' ? parseFloat(amount) : null
      });

      if (response.success) {
        setDepositId(response.deposit_id);
        setDepositSubmitted(true);
      }
    } catch (error) {
      console.error('Deposit submission failed:', error);
      alert('Failed to create deposit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setAmount('');
    setDepositSubmitted(false);
    setDepositId('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="deposit-modal-overlay">
      <div className="deposit-modal">
        <div className="deposit-modal__header">
          <h2 className="deposit-modal__title">Deposit {currency}</h2>
          <button className="deposit-modal__close" onClick={handleClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="#667085" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {!depositSubmitted ? (
          <>
            <div className="deposit-modal__content">
              <div className="deposit-modal__info">
                <div className="deposit-modal__info-item">
                  <span className="deposit-modal__info-label">Currency:</span>
                  <span className="deposit-modal__info-value">{currency}</span>
                </div>
                <div className="deposit-modal__info-item">
                  <span className="deposit-modal__info-label">Network:</span>
                  <span className="deposit-modal__info-value">Bitcoin (BTC)</span>
                </div>
                <div className="deposit-modal__info-item">
                  <span className="deposit-modal__info-label">Min deposit:</span>
                  <span className="deposit-modal__info-value">0.001 BTC</span>
                </div>
              </div>

              <div className="deposit-modal__address">
                <label className="deposit-modal__label">Deposit Address</label>
                <div className="deposit-modal__address-container">
                  <input 
                    type="text" 
                    value={btcAddress} 
                    readOnly 
                    className="deposit-modal__address-input"
                  />
                  <button 
                    className="deposit-modal__copy-btn"
                    onClick={() => navigator.clipboard.writeText(btcAddress)}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M13.333 6h-6C6.597 6 6 6.597 6 7.333v6C6 14.403 6.597 15 7.333 15h6C14.403 15 15 14.403 15 13.333v-6C15 6.597 14.403 6 13.333 6z" stroke="#667085" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M3.333 10h-.666C1.93 10 1 9.07 1 8.333v-6C1 1.597 1.597 1 2.333 1h6C9.07 1 10 1.93 10 2.667V3.333" stroke="#667085" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              </div>

              <div className="deposit-modal__qr">
                <div className="deposit-modal__qr-placeholder">
                  <svg width="100" height="100" viewBox="0 0 100 100" fill="none">
                    <rect x="5" y="5" width="15" height="15" fill="#1570EF"/>
                    <rect x="80" y="5" width="15" height="15" fill="#1570EF"/>
                    <rect x="5" y="80" width="15" height="15" fill="#1570EF"/>
                    <rect x="25" y="25" width="50" height="50" fill="#F2F4F7"/>
                    <text x="50" y="55" textAnchor="middle" fontSize="8" fill="#667085">QR Code</text>
                  </svg>
                </div>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="deposit-modal__amount">
                  <label className="deposit-modal__label">Amount to deposit</label>
                  <div className="deposit-modal__input-group">
                    <input
                      type="number"
                      step="0.00000001"
                      min="0.001"
                      placeholder="0.001"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className="deposit-modal__amount-input"
                      required
                    />
                    <span className="deposit-modal__currency">{currency}</span>
                  </div>
                  {amount && (
                    <p className="deposit-modal__conversion">
                      ≈ ${(parseFloat(amount) * 130000).toFixed(2)} USD
                    </p>
                  )}
                </div>

                <div className="deposit-modal__warning">
                  <div className="deposit-modal__warning-icon">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M8 5.333V8m0 2.667h.007M14.667 8A6.667 6.667 0 1 1 1.333 8a6.667 6.667 0 0 1 13.334 0z" stroke="#F79009" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="deposit-modal__warning-text">
                    Send only {currency} to this address. Sending other cryptocurrencies may result in permanent loss.
                  </div>
                </div>

                <button 
                  type="submit" 
                  className="deposit-modal__submit"
                  disabled={isSubmitting || !amount || parseFloat(amount) < 0.001}
                >
                  {isSubmitting ? 'Processing...' : 'I have sent the payment'}
                </button>
              </form>
            </div>
          </>
        ) : (
          <div className="deposit-modal__success">
            <div className="deposit-modal__success-icon">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <rect width="48" height="48" rx="24" fill="#D1FADF"/>
                <path d="M16 24l6 6L36 16" stroke="#12B76A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3 className="deposit-modal__success-title">Payment Submitted</h3>
            <p className="deposit-modal__success-text">
              Your payment has been submitted for review. Please wait a few minutes as we confirm your payment.
            </p>
            <div className="deposit-modal__success-info">
              <div className="deposit-modal__success-row">
                <span>Deposit ID:</span>
                <span className="deposit-modal__success-id">{depositId}</span>
              </div>
              <div className="deposit-modal__success-row">
                <span>Amount:</span>
                <span>{amount} {currency}</span>
              </div>
              <div className="deposit-modal__success-row">
                <span>Status:</span>
                <span className="deposit-modal__success-status">Pending Confirmation</span>
              </div>
            </div>
            <p className="deposit-modal__success-note">
              You will receive a notification once the payment is confirmed. This usually takes 10-30 minutes.
            </p>
            <button className="deposit-modal__success-btn" onClick={handleClose}>
              Continue
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DepositModal;